import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, Loader2, Check, X, KeyRound, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin + '/api';

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','District of Columbia','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'
];

/**
 * Request Invitation form — gated by (referring member + invite code).
 *
 * Flow:
 *  1. Visitor fills name/email/etc.
 *  2. "Referred by" autocompletes from real Bundle members (mandatory).
 *  3. Visitor enters the invite code shared by their referring member (mandatory).
 *  4. On submit we verify both with the backend. If valid we close this modal and
 *     hand control to the parent (Landing.jsx), which opens the regular SignupModal
 *     with the invite code already filled in.
 */
export function RequestInvitationModal({ isOpen, onClose, onSignInClick, onVerified }) {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    city: '', state: '', handicap: '', home_course: '',
    referred_by: '', referred_by_user_id: '', invite_code: '',
  });

  // ===== Referred-by autocomplete =====
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const debounceRef = useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Debounced lookup on referred_by text changes (clears the locked user_id if they
  // edit after picking, so we re-verify on submit).
  useEffect(() => {
    if (!form.referred_by || form.referred_by.length < 2) {
      setSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setSuggestLoading(true);
        const res = await fetch(`${API_URL}/public/member-autocomplete?q=${encodeURIComponent(form.referred_by)}`);
        if (res.ok) {
          const data = await res.json();
          setSuggestions(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        // Network blip — silently keep previous suggestions
      } finally {
        setSuggestLoading(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [form.referred_by]);

  const pickReferrer = (m) => {
    setForm((f) => ({ ...f, referred_by: m.name, referred_by_user_id: m.user_id }));
    setShowSuggest(false);
  };

  const submit = async (e) => {
    e?.preventDefault();
    // Required fields
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    if (!form.referred_by_user_id) {
      toast.error('Please pick your referring Bundle member from the list');
      return;
    }
    if (!form.invite_code.trim()) {
      toast.error('Please enter your invite code');
      return;
    }
    setLoading(true);
    try {
      // 1) Verify referrer + code together (no detail in error response to deter brute-forcing)
      const verifyRes = await fetch(`${API_URL}/invitation-requests/verify-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          referred_by_user_id: form.referred_by_user_id,
          invite_code: form.invite_code.trim(),
        }),
      });
      const verifyData = await verifyRes.json().catch(() => ({}));
      if (!verifyRes.ok || !verifyData.ok) {
        toast.error(verifyData.message || 'Invite code or referring member not recognised.');
        setLoading(false);
        return;
      }
      // 2) Log the request as an audit record (best effort — non-blocking on failure)
      try {
        await fetch(`${API_URL}/invitation-requests`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...form,
            invite_code: verifyData.invite_code,
            referred_by: verifyData.referrer_name,
            referred_by_user_id: verifyData.referrer_user_id,
          }),
        });
      } catch (e) { /* non-blocking */ }
      // 3) Hand off to parent to open the SignupModal with the code pre-filled
      setSubmitted({ verified: true, invite_code: verifyData.invite_code, referrer: verifyData.referrer_name });
    } catch (err) {
      console.error('verify failed', err);
      toast.error('Could not verify. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setSubmitted(null);
    setForm({ first_name:'',last_name:'',email:'',phone:'',city:'',state:'',handicap:'',home_course:'',referred_by:'',referred_by_user_id:'',invite_code:'' });
    setSuggestions([]);
    onClose?.();
  };

  const handleContinueToSignup = () => {
    const code = submitted?.invite_code || form.invite_code.trim();
    const prefill = {
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      phone: form.phone,
      city: form.city,
      state: form.state,
      handicap: form.handicap,
      home_course: form.home_course,
      referred_by: form.referred_by,
      referred_by_user_id: form.referred_by_user_id,
    };
    close();
    onVerified?.({ invite_code: code, prefill });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="request-invitation-modal">
        <DialogTitle className="sr-only">Request Invitation</DialogTitle>

        {submitted?.verified ? (
          <div className="p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <Check className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-bundle-green mb-1">You're verified ✅</h3>
            <p className="text-sm text-gray-600 mb-5">Referred by <strong>{submitted.referrer}</strong>. Tap below to finish creating your account — your invite code is pre-filled.</p>
            <Button onClick={handleContinueToSignup} data-testid="invite-continue-signup" className="w-full bg-bundle-green hover:bg-bundle-green-dark text-white font-bold">
              Continue to Sign Up →
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col max-h-[90vh]">
            <div className="bg-gradient-to-br from-bundle-green to-emerald-700 text-white px-5 py-4 flex items-center justify-between">
              <div>
                <h2 className="font-heading font-bold text-lg">Request Invitation</h2>
                <p className="text-xs text-white/80 mt-0.5">Got an invite code from a Bundle member? Enter both below to get instant access.</p>
              </div>
              <button type="button" onClick={close} className="text-white/80 hover:text-white" data-testid="invite-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-3 overflow-y-auto">
              {/* ===== GATE FIELDS (mandatory) ===== */}
              <div className="rounded-lg border border-bundle-gold/40 bg-bundle-gold/10 p-3 space-y-3">
                <p className="text-[11px] font-black uppercase tracking-wider text-amber-900">Required to gain access</p>

                <div className="relative">
                  <Label htmlFor="invite-ref" className="text-xs font-semibold text-gray-800">Referring Bundle member *</Label>
                  <div className="relative">
                    <UserIcon className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                    <Input
                      id="invite-ref"
                      data-testid="invite-referred-by"
                      className="pl-8"
                      value={form.referred_by}
                      onChange={(e) => { update('referred_by', e.target.value); update('referred_by_user_id', ''); setShowSuggest(true); }}
                      onFocus={() => setShowSuggest(true)}
                      placeholder="Type their name…"
                      autoComplete="off"
                      required
                    />
                    {form.referred_by_user_id && (
                      <span className="absolute right-2 top-2 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">
                        <Check className="w-3 h-3" /> Verified
                      </span>
                    )}
                  </div>
                  {showSuggest && form.referred_by.length >= 2 && !form.referred_by_user_id && (
                    <div className="absolute z-30 left-0 right-0 mt-1 bg-white rounded-lg border border-gray-200 shadow-lg max-h-44 overflow-y-auto" data-testid="invite-ref-suggestions">
                      {suggestLoading ? (
                        <div className="px-3 py-2 text-xs text-gray-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</div>
                      ) : suggestions.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-gray-500">No matching members. Ask your referrer for their exact name.</div>
                      ) : (
                        suggestions.map(m => (
                          <button
                            key={m.user_id}
                            type="button"
                            onClick={() => pickReferrer(m)}
                            data-testid={`invite-ref-suggest-${m.user_id}`}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-bundle-cream text-gray-800 border-b border-gray-100 last:border-b-0"
                          >
                            {m.name}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                  <p className="text-[10px] text-gray-500 mt-0.5">Pick the member who invited you — required.</p>
                </div>

                <div>
                  <Label htmlFor="invite-code" className="text-xs font-semibold text-gray-800">Invite code *</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                    <Input
                      id="invite-code"
                      data-testid="invite-code-input"
                      className="pl-8 uppercase tracking-widest font-mono"
                      value={form.invite_code}
                      onChange={(e) => update('invite_code', e.target.value.toUpperCase())}
                      placeholder="Enter your invite code"
                      maxLength={40}
                      required
                    />
                  </div>
                  <p className="text-[10px] text-gray-500 mt-0.5">Ask your referring member for the code — required to gain access.</p>
                </div>
              </div>

              {/* ===== Your info ===== */}
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div>
                  <Label htmlFor="invite-first" className="text-xs font-semibold text-gray-700">First name *</Label>
                  <Input id="invite-first" data-testid="invite-first-name" value={form.first_name} onChange={(e) => update('first_name', e.target.value)} required maxLength={60} />
                </div>
                <div>
                  <Label htmlFor="invite-last" className="text-xs font-semibold text-gray-700">Last name *</Label>
                  <Input id="invite-last" data-testid="invite-last-name" value={form.last_name} onChange={(e) => update('last_name', e.target.value)} required maxLength={60} />
                </div>
              </div>

              <div>
                <Label htmlFor="invite-email" className="text-xs font-semibold text-gray-700">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                  <Input id="invite-email" type="email" data-testid="invite-email" className="pl-8" value={form.email} onChange={(e) => update('email', e.target.value)} required />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="invite-phone" className="text-xs font-semibold text-gray-700">Phone</Label>
                  <Input id="invite-phone" type="tel" data-testid="invite-phone" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="optional" />
                </div>
                <div>
                  <Label htmlFor="invite-handicap" className="text-xs font-semibold text-gray-700">Handicap</Label>
                  <Input id="invite-handicap" data-testid="invite-handicap" value={form.handicap} onChange={(e) => update('handicap', e.target.value)} placeholder="e.g. 12" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="invite-city" className="text-xs font-semibold text-gray-700">City</Label>
                  <Input id="invite-city" data-testid="invite-city" value={form.city} onChange={(e) => update('city', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="invite-state" className="text-xs font-semibold text-gray-700">State</Label>
                  <select
                    id="invite-state"
                    data-testid="invite-state"
                    value={form.state}
                    onChange={(e) => update('state', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select…</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="invite-home" className="text-xs font-semibold text-gray-700">Home course</Label>
                <Input id="invite-home" data-testid="invite-home-course" value={form.home_course} onChange={(e) => update('home_course', e.target.value)} placeholder="optional" />
              </div>
            </div>

            <div className="border-t border-gray-100 px-5 py-3 bg-bundle-cream/30 flex flex-col gap-2">
              <Button type="submit" disabled={loading} data-testid="invite-submit-btn" className="w-full bg-bundle-green hover:bg-bundle-green-dark text-white font-bold">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Verifying…</> : 'Verify & Continue'}
              </Button>
              <button type="button" onClick={() => { close(); onSignInClick?.(); }} className="text-xs text-gray-500 hover:text-bundle-green text-center" data-testid="invite-have-account">
                Already a member? <span className="font-bold text-bundle-green">Sign In</span>
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
